import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Trash2, Calendar, Power, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Input } from '../../../components/ui/input';
import { AppLayout } from '../../../components/layout/AppLayout';
import { pickupAPI } from '../../../services/api';
import { toast } from 'sonner';

const RunnerProfileSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runnerProfile, setRunnerProfile] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [availableNow, setAvailableNow] = useState(false);
  
  // Add service area form
  const [selectedCity, setSelectedCity] = useState('');
  const [cityAreas, setCityAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  
  // Temporary area form
  const [tempCity, setTempCity] = useState('');
  const [tempCityAreas, setTempCityAreas] = useState([]);
  const [tempArea, setTempArea] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load runner profile
      const profileRes = await pickupAPI.getMyRunnerProfile();
      setRunnerProfile(profileRes.data.runner_profile);
      setAvailableNow(profileRes.data.runner_profile.available_now || false);

      // Load all cities
      const citiesRes = await pickupAPI.getCities();
      setAllCities(citiesRes.data.cities);
    } catch (error) {
      console.error('Load data error:', error);
      toast.error(error.response?.data?.detail || 'Failed to load profile');
      
      if (error.response?.status === 404) {
        toast.info('You need to apply as a runner first');
        navigate('/pickup/register-driver');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCityChange = async (cityName) => {
    setSelectedCity(cityName);
    setSelectedAreas([]);
    
    if (!cityName) {
      setCityAreas([]);
      return;
    }
    
    try {
      const res = await pickupAPI.getCityAreas(cityName);
      setCityAreas(res.data.areas);
    } catch (error) {
      toast.error('Failed to load areas');
    }
  };

  const handleTempCityChange = async (cityName) => {
    setTempCity(cityName);
    setTempArea('');
    
    if (!cityName) {
      setTempCityAreas([]);
      return;
    }
    
    try {
      const res = await pickupAPI.getCityAreas(cityName);
      setTempCityAreas(res.data.areas);
    } catch (error) {
      toast.error('Failed to load areas');
    }
  };

  const toggleAreaSelection = (area) => {
    setSelectedAreas(prev => 
      prev.includes(area) 
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const handleAddServiceAreas = async () => {
    if (!selectedCity) {
      toast.error('Please select a city');
      return;
    }
    if (selectedAreas.length === 0) {
      toast.error('Please select at least one area');
      return;
    }

    setSaving(true);
    try {
      await pickupAPI.updateRunnerServiceAreas({
        city: selectedCity,
        areas: selectedAreas
      });
      
      toast.success(`Service areas updated for ${selectedCity}`);
      
      // Reset form and reload
      setSelectedCity('');
      setSelectedAreas([]);
      setCityAreas([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update service areas');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCity = async (cityName) => {
    if (!confirm(`Remove ${cityName} from your service areas?`)) return;

    try {
      await pickupAPI.removeRunnerCity(cityName);
      toast.success(`${cityName} removed`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove city');
    }
  };

  const handleToggleAvailability = async (newStatus) => {
    try {
      await pickupAPI.updateRunnerAvailability({ available_now: newStatus });
      setAvailableNow(newStatus);
      toast.success(newStatus ? '✅ You are now AVAILABLE' : 'You are now UNAVAILABLE');
    } catch (error) {
      toast.error('Failed to update availability');
      setAvailableNow(!newStatus); // Revert on error
    }
  };

  const handleAddTemporaryArea = async () => {
    if (!tempCity || !tempArea || !tempEndDate) {
      toast.error('Please fill all temporary area fields');
      return;
    }

    setSaving(true);
    try {
      await pickupAPI.addTemporaryArea({
        city: tempCity,
        area: tempArea,
        end_date: tempEndDate
      });
      
      toast.success('Temporary area added');
      
      // Reset form and reload
      setTempCity('');
      setTempArea('');
      setTempEndDate('');
      setTempCityAreas([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add temporary area');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTemporaryArea = async (city, area) => {
    if (!confirm(`Remove temporary area ${city}/${area}?`)) return;

    try {
      await pickupAPI.removeTemporaryArea(city, area);
      toast.success('Temporary area removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove temporary area');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!runnerProfile) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card>
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Runner Profile Not Found</h3>
              <p className="text-muted-foreground mb-4">You need to apply as a runner first</p>
              <Button onClick={() => navigate('/pickup/register-driver')}>
                Apply to be a Runner
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const serviceCities = runnerProfile.service_cities || [];
  const temporaryAreas = runnerProfile.temporary_areas || [];

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6 p-4">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-2">Runner Settings</h1>
          <p className="text-muted-foreground">Manage your service areas and availability</p>
        </div>

        {/* Availability Toggle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Power className="h-5 w-5" />
              Availability Status
            </CardTitle>
            <CardDescription>
              Control whether you receive new job alerts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <div className="font-semibold mb-1">
                  {availableNow ? (
                    <span className="text-green-600 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5" /> Available Now
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Currently Unavailable</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {availableNow 
                    ? 'You will receive job alerts in your service areas' 
                    : 'Turn on to start receiving job alerts'}
                </p>
              </div>
              <Switch 
                checked={availableNow} 
                onCheckedChange={handleToggleAvailability}
              />
            </div>
          </CardContent>
        </Card>

        {/* Current Service Areas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              My Service Areas
            </CardTitle>
            <CardDescription>
              Cities and areas where you operate
            </CardDescription>
          </CardHeader>
          <CardContent>
            {serviceCities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No service areas added yet</p>
                <p className="text-sm">Add cities and areas below to start receiving jobs</p>
              </div>
            ) : (
              <div className="space-y-4">
                {serviceCities.map((cityData, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold">{cityData.city}</h4>
                        <p className="text-sm text-muted-foreground">
                          {cityData.areas.length} area{cityData.areas.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveCity(cityData.city)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {cityData.areas.map((area, aIdx) => (
                        <Badge key={aIdx} variant="secondary">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Service Areas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Service Areas
            </CardTitle>
            <CardDescription>
              Select a city and the areas you serve
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>City</Label>
              <Select value={selectedCity} onValueChange={handleCityChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {allCities.map((city) => (
                    <SelectItem key={city.name} value={city.name}>
                      {city.name}, {city.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {cityAreas.length > 0 && (
              <div>
                <Label className="mb-2 block">
                  Select Areas (tap to toggle)
                </Label>
                <div className="max-h-64 overflow-y-auto border rounded-lg p-3 space-y-2">
                  {cityAreas.map((area) => (
                    <div
                      key={area}
                      onClick={() => toggleAreaSelection(area)}
                      className={`
                        p-3 rounded-md cursor-pointer transition-all
                        ${selectedAreas.includes(area)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/70'
                        }
                      `}
                    >
                      {area}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {selectedAreas.length} area{selectedAreas.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            )}

            <Button 
              onClick={handleAddServiceAreas}
              disabled={!selectedCity || selectedAreas.length === 0 || saving}
              className="w-full"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Service Areas'}
            </Button>
          </CardContent>
        </Card>

        {/* Temporary Areas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Temporary Areas
            </CardTitle>
            <CardDescription>
              Add areas you'll serve temporarily (e.g., when visiting another city)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {temporaryAreas.length > 0 && (
              <div className="space-y-2 mb-4">
                {temporaryAreas.map((temp, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div>
                      <div className="font-medium">{temp.city} - {temp.area}</div>
                      <div className="text-sm text-muted-foreground">
                        Until {new Date(temp.end_date).toLocaleDateString()}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleRemoveTemporaryArea(temp.city, temp.area)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label>City</Label>
                <Select value={tempCity} onValueChange={handleTempCityChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCities.map((city) => (
                      <SelectItem key={city.name} value={city.name}>
                        {city.name}, {city.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {tempCityAreas.length > 0 && (
                <div>
                  <Label>Area</Label>
                  <Select value={tempArea} onValueChange={setTempArea}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      {tempCityAreas.map((area) => (
                        <SelectItem key={area} value={area}>
                          {area}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>End Date</Label>
                <Input 
                  type="date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <Button 
                onClick={handleAddTemporaryArea}
                disabled={!tempCity || !tempArea || !tempEndDate || saving}
                className="w-full"
                variant="secondary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Temporary Area
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900 dark:text-blue-300 space-y-2">
                <p><strong>How it works:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Add cities and areas where you operate</li>
                  <li>Toggle "Available Now" to start/stop receiving job alerts</li>
                  <li>Changes apply immediately to new jobs</li>
                  <li>Jobs you've already accepted stay with you</li>
                  <li>Temporary areas automatically expire on the end date</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default RunnerProfileSettings;
